import clsx from "clsx";
import { Footer } from "components/Footer";
import { Navbar } from "components/Navbar";
import { GlobalContext } from "contexts/GlobalContext";
import { useScrollToTop } from "hooks/useScrollToTop";
import { ReactNode, useEffect, useContext, PropsWithChildren } from "react";

export const pageLoadMessageKey = "page-load-message";

export const setPageLoadMessage = (message: string) => localStorage.setItem(pageLoadMessageKey, message);

export const Page = (
    props: PropsWithChildren<{
        title: string;
        description: string;
        canonical: string;
        imageUrl?: string;
        subNav?: ReactNode;
        preNav?: ReactNode;
        navClassName?: string;
        useContainer?: boolean;
        noIndex?: boolean;
        hideBottomPadding?: boolean;
        hideTopPadding?: boolean;
        hideNavigation?: boolean;
    }>,
) => {
    const {
        children,
        title,
        description,
        canonical,
        subNav,
        navClassName,
        useContainer = true,
        noIndex,
        hideTopPadding,
        hideBottomPadding,
        hideNavigation,
        preNav,
    } = props;

    const { addNotification } = useContext(GlobalContext);

    useScrollToTop();

    // set the title
    useEffect(() => {
        const titleElement = document.querySelector("title");
        // const ogTitleElement = document.querySelector("meta[property='og:title']");

        const siteName = "Frankenstories";

        if (titleElement) {
            // we only want to show the site name on home page
            const fullTitle = title.length ? title : `${siteName} - A creative writing game for the whole class`;

            titleElement.textContent = fullTitle;
        }
    }, [title]);

    // set the description
    useEffect(() => {
        const descriptionElement = document.querySelector("meta[name='description']");
        const ogDescriptionElement = document.querySelector("meta[property='og:description']");

        if (descriptionElement) {
            descriptionElement.setAttribute("content", description ?? "");
        }

        if (ogDescriptionElement) {
            ogDescriptionElement.setAttribute("content", description ?? "");
        }
    }, [description]);

    // set canonical url
    useEffect(() => {
        const fullCanonical = `https://${window.location.host}${canonical}`;

        const canonicalElement = document.querySelector("link[rel='canonical']");
        const ogCanonicalElement = document.querySelector("meta[property='og:url']");

        if (canonicalElement) {
            canonicalElement.setAttribute("href", fullCanonical);
        }

        if (ogCanonicalElement) {
            ogCanonicalElement.setAttribute("content", fullCanonical ?? "");
        }
    }, [canonical]);

    // manage noindex tags
    useEffect(() => {
        const metaTag = document.querySelector("meta[name='robots'][content='noindex']");

        // add tag if required
        if (noIndex && !metaTag) {
            const newMetaTag = document.createElement("meta");
            newMetaTag.setAttribute("name", "robots");
            newMetaTag.setAttribute("content", "noindex");
            document.head.appendChild(newMetaTag);
        } else if (!noIndex && metaTag) {
            document.head.removeChild(metaTag);
        }
    }, [noIndex]);

    // handle page load message
    useEffect(() => {
        const pageLoadMessage = localStorage.getItem(pageLoadMessageKey);

        if (pageLoadMessage) {
            addNotification({
                type: "success",
                title: pageLoadMessage,
            });

            localStorage.removeItem(pageLoadMessageKey);
        }
    }, [addNotification]);

    return (
        <div className="d-flex flex-grow-1 flex-column h-100">
            <a href="#main-content" className="skip-link">
                Skip to content
            </a>

            {!hideNavigation && (
                <div className="d-print-none">
                    {preNav}
                    <Navbar className={navClassName} />
                    {subNav}
                </div>
            )}

            <div
                id="main-content"
                className={clsx(
                    useContainer && "container",
                    "d-flex flex-grow-1 position-relative",
                    !hideTopPadding && "pt-4 pt-lg-5",
                    !hideBottomPadding && "pb-4 pb-lg-5",
                )}
            >
                <div className="d-flex flex-grow-1 flex-column position-relative w-100">{children}</div>
            </div>

            {!hideNavigation && <Footer />}
        </div>
    );
};
